<template>
  <div class="col-sm-12 pd-5">
    <div class="card p-1 mb-0 shadow-dashboard">
      <div class="card-header bg-white d-flex justify-content-between align-items-center py-2 border-bottom">
        <h5 class="font-bold mb-0">{{ title }}</h5>
        <div class="d-flex align-items-center">
          <button @click="$emit('print')" class="btn btn-sm btn-outline-secondary">
             <i class="feather icon-printer mr-25"></i> Print
          </button>
        </div>
      </div>
      
      <div class="card-body p-0">
        <div class="row mt-1 mb-1 px-1 align-items-end">
          <div class="col-md-6 col-lg-5">
            <label class="col-form-label text-left d-block font-bold">Select Project</label>
            <v-select
              placeholder="Select Project"
              :options="projects"
              label="name"
              :reduce="proj => proj.id"
              :modelValue="projectId"
              @update:modelValue="updateProjectId"
              class="v-select-sm"
            />
          </div>
          <div class="col-md-6 col-lg-7 d-flex justify-content-end">
             <slot name="controls"></slot>
          </div>
        </div>

        <div v-if="loadingProjects || loadingData" class="text-center py-5">
           <div class="spinner-border text-primary" role="status"></div>
           <p class="mt-1">{{ loadingText }}</p>
        </div>

        <div v-else-if="projectId && hasData" class="px-1 pb-1">
           <slot></slot>
        </div>
        
        <div v-else-if="projectId && !loadingData" class="text-center py-5">
          <i class="feather icon-alert-circle text-muted mb-1 large-icon"></i>
          <p class="text-muted">{{ noDataText }}</p>
        </div>
        
        <div v-else-if="!loadingData" class="text-center py-5">
           <div class="mb-2">
             <i :class="['feather', emptyIcon, 'text-light-primary extra-large-icon']"></i>
           </div>
           <h6 class="text-muted">{{ selectPrompt }}</h6>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue'
import { useRoute } from 'vue-router'
import { useSharedProjectList } from "@/services/modules/procurement/useSharedProjectList";

const props = defineProps({
  title: String,
  projectId: [Number, String],
  loadingData: Boolean,
  hasData: Boolean,
  loadingText: { type: String, default: 'Loading data...' },
  noDataText: { type: String, default: 'No data found for this project.' },
  selectPrompt: { type: String, default: 'Please select a project to analyze performance' },
  emptyIcon: { type: String, default: 'icon-clipboard' }
});

const emit = defineEmits(['update:projectId', 'selected', 'print']);

const route = useRoute();
const showError = inject('showError');

const { projects, getProjects, isFetching: loadingProjects } = useSharedProjectList();

const updateProjectId = (val) => {
  emit('update:projectId', val);
  if (val) {
    emit('selected', val);
  }
};

onMounted(async () => {
  try {
    const query = `?company_id=${route.params.companyId}&status=active&is_desc=true`;
    const res = await getProjects(query);
    if (!res.status) return;

    if (projects.value.length < 1) return;

    if (!props.projectId) {
      const firstId = projects.value[0].id;
      emit('update:projectId', firstId);
      emit('selected', firstId);
    }
  } catch (error) {
    showError('Failed to load projects');
  }
});
</script>

<style scoped>
.shadow-dashboard {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.05);
}
.font-bold {
  font-weight: 600;
}
.text-primary {
  color: #7367f0 !important;
}
.cursor-pointer {
  cursor: pointer;
}
.text-light-primary {
    color: #e4e2fb;
}
.large-icon {
    font-size: 2rem;
}
.extra-large-icon {
    font-size: 3rem;
    opacity: 0.3;
}
.v-select-sm :deep(.vs__dropdown-toggle) {
  min-height: 32px;
}
.v-select-sm :deep(.vs__selected) {
    font-size: 0.85rem;
}
</style>
